import React from 'react';
import type { Point, CategorieMarqueur } from '../../types/map';
import { MarkerPopup } from './MarkerPopup';
import { MarkerModale } from './MarkerModale';
import { MarkerPanneau } from './MarkerPanneau';

export type MarkerDisplayMode = 'popup' | 'modale' | 'panneau';

interface MarkerInfoDisplayProps {
  point: Point;
  categorie?: CategorieMarqueur;
  /** Callback pour fermer modale/panneau */
  onClose: () => void;
}

/**
 * Orchestrateur : détermine le mode d'affichage selon la catégorie du point
 * et rend le composant approprié (popup, modale ou panneau).
 *
 * - `popup`   → rendu dans un <Popup> Leaflet (utilisé à l'intérieur du Marker)
 * - `modale`  → overlay centré (rendu hors du MapContainer)
 * - `panneau` → panneau latéral coulissant (rendu hors du MapContainer)
 *
 * Si aucune catégorie n'est définie, utilise le mode popup par défaut.
 */
export const MarkerInfoDisplay: React.FC<MarkerInfoDisplayProps> = ({
  point,
  categorie,
  onClose,
}) => {
  const mode: MarkerDisplayMode = categorie?.modeAffichage || 'popup';

  switch (mode) {
    case 'modale':
      return <MarkerModale point={point} categorie={categorie} onClose={onClose} />;
    case 'panneau':
      return <MarkerPanneau point={point} categorie={categorie} onClose={onClose} />;
    case 'popup':
    default:
      return <MarkerPopup point={point} categorie={categorie} />;
  }
};

/**
 * Détermine si le mode d'affichage nécessite un rendu hors du MapContainer.
 * Popup est géré à l'intérieur de Leaflet, modale/panneau sont rendus en dehors.
 */
export function isExternalMode(categorie?: CategorieMarqueur): boolean {
  const mode = categorie?.modeAffichage || 'popup';
  return mode === 'modale' || mode === 'panneau';
}
